import { map, filter, flatMap, uniq, pickBy, values } from "../../utils/lodash";
import {
    createElementWithSettings,
    createElementWithProperties,
    createSkippable,
    createTranslatable,
} from "./elementFactory";
import { QuestionModel } from "../QuestionModel";
import { QuestionnaireModel } from "../QuestionnaireModel";
import { QuestionType } from "../enums/QuestionType";
import { ActionModel } from "../ActionModel";
import { QuestionActionType } from "../enums/QuestionActionType";
import { createLogic } from "./logicFactory";
import { ChoiceModel } from "../ChoiceModel";
import { getSettingBoolean, getSettingString } from "../utilities/settingUtilities";
import { PropertyType } from "../enums/PropertyType";
import { VariableModel } from "../VariableModel";
import { findVariables } from "../../expressions/parser";
import { SelectionModel } from "../SelectionModel";
import { AxisModel } from "../AxisModel";
import { createRandomization } from "./randomizationFactory";
import { ColumnType } from "../enums/ColumnType";
import { RowType } from "../enums/RowType";
import { createLookupTableFilter } from "./lookupTableFilterFactory";
import { ElementWithSettings } from "../ElementWithSettings";
import { VariableType } from "../enums/VariableType";
import { VariableDataType } from "../enums/VariableDataType";

function questionId(question) {
    return `Q_${question.name.toUpperCase()}`;
}

function variableId(variable) {
    return `V_${variable.name}`;
}

function choiceId(variable, choice) {
    return choice.value
        ? `V_${variable.name}_C_${choice.value.replace(/\s/g, "_").toUpperCase()}`
        : `V_${variable.name}_H_${choice.index}`;
}

function createVariable(
    index: number,
    v: any,
    choiceLists: { [key: number]: ChoiceModel[] },
    el?: any,
): VariableModel {
    const choices = (choiceLists[v.choiceListId] || []).map((item) => ({
        ...item,
        elementId: choiceId(v, item),
    }));
    const rowOpenEndDisplayLogic = el?.rows?.find(
        (r: AxisModel) =>
            getSettingString(r, "OpenEndVariable") === v.name &&
            !getSettingBoolean(r, "DisplayIfDisableInstead"),
    )?.openEndDisplayIf;
    return {
        ...createElementWithSettings(v),
        ...createSkippable(v, rowOpenEndDisplayLogic),
        ...createTranslatable(v),
        index,
        id: v.id,
        elementId: variableId(v),
        choiceListRandomization: createRandomization(v.choiceListRandomization),
        choiceList: choices,
        maxMention: v.maxMention,
        type: v.type ?? VariableType.Discrete,
        dataType: v.dataType as VariableDataType,
    };
}

function createColumn(
    index: number,
    variables: VariableModel[],
    rows: AxisModel[],
    el?: any,
): AxisModel {
    const a = map(rows, (r) => r.variables[index]);
    const b = filter(a, (v) => !!v);
    return {
        ...createElementWithSettings(el),
        ...createSkippable(el),
        ...createTranslatable(el),
        index,
        type: ColumnType[el.type as string],
        variables: uniq(b),
    };
}

function createRow(index: number, variables: VariableModel[], el?: any): AxisModel {
    return {
        ...createElementWithSettings(el),
        ...createSkippable(el),
        ...createTranslatable(el),
        index,
        type: RowType[el.type as string],
        openEndDisplayIf: createLogic(el.openEndDisplayIf),
        variables: map(el.variableIds, (e: number) => variables[e]),
    };
}

function createSelection(el?: any): SelectionModel {
    return {
        ...createElementWithProperties(el),
        value: el.value,
        condition: createLogic(el.condition),
        priority: el.priority,
        score: createLogic(el.score),
    };
}

function createAction(
    questionName: string,
    isPreload: boolean,
    index: number,
    el?: any,
): ActionModel {
    return {
        ...createElementWithProperties(el),
        type: QuestionActionType[el.type as string] || QuestionActionType.BranchTo,
        questionName,
        isPreload,
        condition: createLogic(el.condition),
        selections: map(el.selections, createSelection),
        selectionVariables: map(el.selectionVariables, createElementWithProperties),
    };
}

function getSettingVariables(settings: any) {
    const a = pickBy(settings, (value, key) => key.endsWith("VariableName"));
    return values(a);
}

function getActionVariables(actions: any[]) {
    return filter(
        actions,
        (a: any) => QuestionActionType[a.type as string] === QuestionActionType.ComputeVariable,
    ).map((a) => a.properties[PropertyType[PropertyType.CalculatedVariableName]]);
}

export function getAllDisplayLogicVariables(variables: VariableModel[], questionnaire: QuestionnaireModel) {
    return flatMap(variables, (variable) => [
        ...findVariables(variable.displayLogic, questionnaire),
        ...findVariables(variable.skipLogic, questionnaire),
        ...flatMap(variable.choiceList, (choice) => [
            ...findVariables(choice.displayLogic, questionnaire),
            ...findVariables(choice.skipLogic, questionnaire),
        ]),
    ]);
}

function getVariableNames(q: any): string[] {
    // Populate variable names.
    const a = [
        ...getActionVariables(q.preLoadActions),
        ...getSettingVariables(q.settings),
        ...map(q.variables, (v: any) => v.name),
        ...flatMap(q.rows, (r: any) => map(r.variables, (v: VariableModel) => v.name)),
        ...map(q.cards, (c: any) => c.name),
        ...map(q.dropdowns, (c: any) => c.name),
        ...map(q.areas, (c: any) => c.name),
        ...map(q.lookupColumns, (c: any) => c.name),
        ...flatMap(q.variables, (c: VariableModel) =>
            map(c.choiceList, (a: ChoiceModel) => getSettingString(a, "OpenEndVariable")),
        ),
        ...map(q.rows, (r: AxisModel) => getSettingString(r, "OpenEndVariable")),
        ...getActionVariables(q.postAnswerActions),
    ];
    const b = filter(a, (v) => !!v);
    return uniq(b);
}

export function getVariableDependencies(question: QuestionModel, questionnaire: QuestionnaireModel): string[] {
    const { columns = [] } = question;
    const elements: ElementWithSettings[] = [question, ...columns, ...question.variables];
    const eliminateChoiceVariables = elements
        .map((element) => [
            ...getSettingString(element, "EliminateChoicesIfVariablesAnswered", "").split(","),
            ...getSettingString(element, "EliminateChoicesIfVariablesNotAnswered", "").split(","),
        ])
        .reduce((acc, value) => [...acc, ...value], []);

    // Populate variable dependencies.
    const a = [
        ...eliminateChoiceVariables,
        ...getSettingString(question, "RowEliminationIfVariablesAnswered", "").split(","),
        ...getSettingString(question, "RowEliminationIfVariablesNotAnswered", "").split(","),
        ...getSettingString(question, "ColumnEliminationIfVariablesAnswered", "").split(","),
        ...getSettingString(question, "ColumnEliminationIfVariablesNotAnswered", "").split(","),
        // Get all variables used in display logic.
        ...getAllDisplayLogicVariables(question.variables, questionnaire),
        ...flatMap(question.rows, (row) => [
            ...findVariables(row.displayLogic, questionnaire),
            ...findVariables(row.skipLogic, questionnaire),
            ...(row.openEndDisplayIf ? findVariables(row.openEndDisplayIf, questionnaire) : []),
        ]),
        ...flatMap(question.columns, (column) => [
            ...findVariables(column.displayLogic, questionnaire),
            ...findVariables(column.skipLogic, questionnaire),
            ...getAllDisplayLogicVariables(column.variables, questionnaire),
        ]),
        ...findVariables(question.displayLogic, questionnaire),
        ...findVariables(question.skipLogic, questionnaire),
    ];
    const b = filter(a, (v) => !!v);
    return uniq(b);
}

export function createQuestion(
    index: number,
    blockIndex: number,
    choiceLists: ChoiceModel[][],
    el?: any,
): QuestionModel {
    const elementName = el.name.toUpperCase();
    const variables = map(el.variables, (v: any, i: number) =>
        createVariable(i, v, choiceLists, el),
    );
    const rows = map(el.rows, (r: any, i: number) => createRow(i, variables, r));
    const question: QuestionModel = {
        ...createElementWithSettings(el),
        ...createSkippable(el),
        ...createTranslatable(el),
        index,
        id: el.id,
        elementId: questionId(el),
        name: elementName,
        type: QuestionType[el.type as string],
        blockIndex,
        isPageBreak: el.isPageBreak,
        preLoadActions: map(el.preLoadActions, (a, i: number) =>
            createAction(elementName, true, i, a),
        ),
        postAnswerActions: map(el.postAnswerActions, (a, i: number) =>
            createAction(elementName, false, i, a),
        ),
        variableRandomization: createRandomization(el.variableRandomization),
        choiceListRandomization: createRandomization(el.choiceListRandomization),
        rowRandomization: createRandomization(el.rowRandomization),
        columnRandomization: createRandomization(el.columnRandomization),
        rows,
        columns: map(el.columns, (c: any, i: number) => createColumn(i, variables, rows, c)),
        lookupFilter: createLookupTableFilter(el.lookupFilter, variables),
        variables,
        variableNames: [],
        variableDependencies: [],
    };
    question.variableNames = getVariableNames(question);
    return question;
}
